// Premium Owner Settlement Receipt — Enterprise Edition (matches Internal Ledger tier)
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BRAND, rgb, fmtINR, fmtDate, fmtDateTime, computeDocHash,
  roundRectFill, roundRectFS, drawDiamond, drawStatusPill,
} from './shared';
import { formatHoursHm } from '../durationHours';

const COMPANY = {
  name: 'DEVELOPMENT EXPRESS',
  tagline: 'Development Express · Enterprise Edition',
  md: 'Om Chavan | B.Tech (Civil) | Managing Director',
  addr: 'Karad, Satara, Maharashtra - 415110',
  phone: '+91-8408000084',
  email: 'om.chavan2026@zohomail.in',
  web: 'developmentexpress.in',
};

const DUMMY_PAN = new Set(['ABCDE1234F']);
const DUMMY_IFSC = new Set(['SBIN0001234']);
const DUMMY_REG = new Set(['MH-09-AB-1234']);
const BANK_INCOMPLETE = '⚠ Bank details incomplete';
const PAN_MISSING = '⚠ PAN not provided';

const MARGIN = { top: 25, bottom: 20, left: 15, right: 15 };
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN.left - MARGIN.right; // 180mm

function displayOwnerPan(value) {
  const v = String(value || '').trim().toUpperCase();
  if (!v || v === '—' || DUMMY_PAN.has(v)) return PAN_MISSING;
  return String(value).trim();
}

function displayOwnerBank(value) {
  const v = String(value || '').trim();
  if (!v || v === '—' || v.includes('12345678901')) return BANK_INCOMPLETE;
  return v;
}

function displayOwnerIfsc(value) {
  const v = String(value || '').trim().toUpperCase();
  if (!v || v === '—' || DUMMY_IFSC.has(v)) return BANK_INCOMPLETE;
  return String(value).trim();
}

function displayRegNo(value) {
  const v = String(value || '').trim();
  if (!v || DUMMY_REG.has(v)) return '—';
  return v;
}

const pct = (n, p) => Math.round(Number(n) * p / 100);

const drawCoverPage = (doc, data) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  doc.setFillColor(...rgb(BRAND.COVER_BG));
  doc.rect(0, 0, w, h, 'F');

  doc.setDrawColor(31, 27, 18);
  doc.setLineWidth(0.1);
  for (let i = 0; i < h; i += 2.5) {
    doc.line(0, i, w, i + 1);
  }

  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.5);
  [[15, 15, 15, 32, 32, 15], [w - 15, 15, w - 15, 32, w - 32, 15],
    [15, h - 15, 15, h - 32, 32, h - 15], [w - 15, h - 15, w - 15, h - 32, w - 32, h - 15]]
    .forEach(([x1, y1, x2, y2, x3, y3]) => {
      doc.line(x1, y1, x2, y2);
      doc.line(x1, y1, x3, y3);
    });

  const emblemY = 52;
  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.6);
  doc.circle(w / 2, emblemY, 18, 'S');
  doc.setLineWidth(0.25);
  doc.circle(w / 2, emblemY, 14, 'S');
  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('DE', w / 2, emblemY + 2, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.text('SINCE 2011', w / 2, emblemY + 8, { align: 'center' });

  const bandY = h - 188;
  doc.setFillColor(...rgb(BRAND.COVER_GOLD));
  doc.rect(0, bandY, w, 0.3, 'F');
  doc.rect(0, bandY + 28, w, 0.3, 'F');

  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(COMPANY.name, w / 2, bandY + 10, { align: 'center' });
  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('M A C H I N E   O S   ·   O W N E R   S E T T L E M E N T', w / 2, bandY + 17, { align: 'center' });

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('O F F I C I A L   P A Y M E N T   R E C E I P T', w / 2, bandY + 38, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('Owner Settlement', w / 2, bandY + 52, { align: 'center' });
  doc.text('Statement', w / 2, bandY + 62, { align: 'center' });

  const dy = bandY + 74;
  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.4);
  doc.line(w / 2 - 30, dy, w / 2 - 4, dy);
  doc.line(w / 2 + 4, dy, w / 2 + 30, dy);
  drawDiamond(doc, w / 2, dy, 1.5, BRAND.COVER_GOLD);

  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Net Amount Credited', w / 2, dy + 12, { align: 'center' });
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text(fmtINR(data.net), w / 2, dy + 24, { align: 'center' });

  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Settlement Period: ${data.period}`, w / 2, dy + 34, { align: 'center' });

  const infoY = h - 42;
  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('RECEIPT NO.', 20, infoY);
  doc.text('OWNER', w / 2, infoY, { align: 'center' });
  doc.text('BOOKING REF', w - 20, infoY, { align: 'right' });

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(data.receiptNo, 20, infoY + 5);
  doc.text(data.ownerName, w / 2, infoY + 5, { align: 'center' });
  doc.text(data.bookingId, w - 20, infoY + 5, { align: 'right' });

  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.text(`${COMPANY.addr}  ·  ${COMPANY.phone}`, w / 2, h - 18, { align: 'center' });
};

const drawInsideHeaderFooter = (doc, page, totalPages, receiptNo, docHash) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const left = MARGIN.left;
  const right = w - MARGIN.right;

  doc.setFillColor(...rgb(BRAND.GOLD));
  doc.rect(0, 0, w, 4, 'F');
  doc.setFillColor(...rgb(BRAND.GOLD_DEEP));
  doc.rect(0, 4, w, 0.5, 'F');

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('DEVELOPMENT EXPRESS', left, 11);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('Development Express  ·  Owner Settlement', left, 15);

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Owner Payment Receipt - Detail', w / 2, 13, { align: 'center' });

  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text('OFFICIAL  ·  SETTLEMENT', right, 11, { align: 'right' });
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.text(receiptNo, right, 15, { align: 'right' });

  doc.setDrawColor(...rgb(BRAND.BORDER));
  doc.setLineWidth(0.2);
  doc.line(left, 18, right, 18);

  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.4);
  doc.line(left, h - 16, w / 2 - 4, h - 16);
  doc.line(w / 2 + 4, h - 16, right, h - 16);
  drawDiamond(doc, w / 2, h - 16, 1.3, BRAND.GOLD);

  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text(`${COMPANY.web}  ·  ${COMPANY.email}`, left, h - 11);
  doc.text('(c) 2026 Development Express. All rights reserved.', w / 2, h - 11, { align: 'center' });
  doc.text(`Page ${page} of ${totalPages}`, right, h - 11, { align: 'right' });
  doc.setTextColor(...rgb(BRAND.MUTED_LT));
  doc.setFontSize(5.5);
  doc.text(`Audit: ${docHash.substring(0, 16)}`, left, h - 6);
  doc.text(`Generated ${fmtDateTime(new Date().toISOString())}`, right, h - 6, { align: 'right' });
};

const drawKPICard = (doc, x, y, w, h, label, value, sub, accent) => {
  roundRectFill(doc, x + 0.4, y + 0.4, w, h, 1.5, '#EFEFEF');
  roundRectFS(doc, x, y, w, h, 1.5, '#FFFFFF', BRAND.BORDER, 0.2);
  doc.setFillColor(...rgb(accent));
  doc.rect(x, y, w, 1, 'F');
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.text(label.toUpperCase(), x + 3, y + 5);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(value, x + 3, y + 12);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  const subLines = doc.splitTextToSize(String(sub || ''), w - 6);
  doc.text(subLines[0], x + 3, y + 17);
};

const drawInfoCard = (doc, x, y, w, h, title, rows) => {
  roundRectFS(doc, x, y, w, h, 1.5, '#FFFFFF', BRAND.BORDER, 0.25);
  doc.setFillColor(...rgb(BRAND.GOLD_PALE));
  doc.rect(x, y, w, 7, 'F');
  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.15);
  doc.line(x, y + 7, x + w, y + 7);
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text(title.toUpperCase(), x + 3, y + 4.8);
  rows.forEach(([k, v], i) => {
    const ry = y + 12 + i * 6;
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(k, x + 3, ry);
    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    const lines = doc.splitTextToSize(String(v || '—'), w - 32);
    doc.text(lines[0], x + 28, ry);
  });
};

export async function generatePremiumOwnerReceipt(booking = {}, options = {}) {
  const { download = true } = options;
  const gross = Number(booking.grossAmount || 105000);
  const commPct = Number(booking.commissionPct) || 0;
  const useDbAmounts = Boolean(
    booking.useDbAmounts
    || booking.commissionAmount != null
    || booking.netAmount != null
    || booking.netOwnerAmount != null,
  );
  const comm = useDbAmounts
    ? Number(booking.commissionAmount ?? 0)
    : pct(gross, commPct);
  const tds = useDbAmounts
    ? Number(booking.tdsAmount ?? 0)
    : pct(gross, 2);
  const gstTcs = useDbAmounts
    ? Number(booking.tcsAmount ?? booking.gstTcsAmount ?? 0)
    : pct(gross, 1);
  const deductions = comm + tds + gstTcs;
  const net = useDbAmounts
    ? Number(booking.netAmount ?? booking.netOwnerAmount ?? gross - deductions)
    : gross - deductions;
  const effectiveCommPct = gross > 0 && useDbAmounts
    ? Math.round((comm / gross) * 1000) / 10
    : commPct;

  const receiptNo = booking.receiptNo || 'DE/RCPT/OWN/2026/041001';
  const bookingId = booking.bookingId || 'BK-2026-041001';
  const payDate = booking.payDate || fmtDate(new Date().toISOString());
  const period = booking.period || 'Apr 2026';
  const ownerName = booking.ownerName || 'Rajesh Patil';
  const docHash = await computeDocHash(receiptNo, period, net);

  const data = {
    receiptNo, bookingId, payDate, period, ownerName, gross, comm, tds, gstTcs, deductions, net,
    commPct: effectiveCommPct,
    hours: booking.hours || 75,
    ratePerHour: booking.ratePerHour || 1400,
    finalBase: Number(booking.finalBase || 0),
    finalGst: Number(booking.finalGst || 0),
    words: booking.words || 'Rupees Eighty Six Thousand One Hundred Only',
    ownerPan: displayOwnerPan(booking.ownerPan),
    ownerAadhaar: booking.ownerAadhaar || 'XXXX-XXXX-4521',
    ownerBank: displayOwnerBank(booking.ownerBank),
    ownerIfsc: displayOwnerIfsc(booking.ownerIfsc),
    machineName: booking.machineName || 'JCB 3DX Backhoe Loader',
    regNo: displayRegNo(booking.regNo),
    machineType: booking.machineType || 'Backhoe Loader',
    clientName: booking.clientName || 'Patil Builders Pvt. Ltd.',
    workPeriod: booking.workPeriod || '01-10 Apr 2026',
    paymentMode: booking.paymentMode || 'Wallet Credit',
    paymentRef: booking.paymentRef || booking.settlementId || receiptNo,
    walletDestination: booking.walletDestination || 'Development Express Owner Wallet',
  };

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth() || PAGE_W;
  const contentW = (w - MARGIN.left - MARGIN.right) || CONTENT_W;

  drawCoverPage(doc, data);

  doc.addPage();
  let y = MARGIN.top;

  const ensureSpace = (neededMm) => {
    const pageH = doc.internal.pageSize.getHeight() || PAGE_H;
    if (y + neededMm > pageH - MARGIN.bottom) {
      doc.addPage();
      y = MARGIN.top;
    }
  };

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Settlement Breakdown', MARGIN.left, y);
  const settlementStatus = String(booking.status || 'settled').toLowerCase();
  const statusLabel = settlementStatus === 'settled'
    ? 'Paid'
    : settlementStatus === 'pending_transfer'
      ? 'Pending'
      : settlementStatus === 'failed'
        ? 'Failed'
        : 'Recorded';
  const statusFill = settlementStatus === 'settled'
    ? BRAND.EMERALD_LT
    : settlementStatus === 'failed'
      ? BRAND.RUBY
      : BRAND.GOLD_PALE;
  const statusInk = settlementStatus === 'settled'
    ? BRAND.EMERALD
    : settlementStatus === 'failed'
      ? BRAND.RUBY
      : BRAND.GOLD_DARK;
  drawStatusPill(doc, w - MARGIN.right - 28, y - 4, 28, 6, statusLabel, statusFill, statusInk);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(`Payment Date: ${payDate}  ·  Period: ${period}`, MARGIN.left, y + 5);
  y += 12;

  const cardW = (contentW - 8) / 3;
  const grossSub = data.finalBase > 0
    ? `Base ₹${fmtINR(data.finalBase)} + GST ₹${fmtINR(data.finalGst)}`
    : data.hours > 0 && data.ratePerHour > 0
      ? `${formatHoursHm(data.hours)} @ Rs ${data.ratePerHour}/hr`
      : 'Per settlement ledger';
  drawKPICard(doc, MARGIN.left, y, cardW, 20, 'Gross Billing', fmtINR(gross), grossSub, BRAND.GOLD);
  drawKPICard(doc, MARGIN.left + cardW + 4, y, cardW, 20, 'Deductions', fmtINR(deductions), 'Commission + TDS + GST TCS', BRAND.RUBY);
  drawKPICard(doc, MARGIN.left + (cardW + 4) * 2, y, cardW, 20, 'Net Paid', fmtINR(net), 'Credited to owner wallet', BRAND.EMERALD);
  y += 26;

  const halfW = (contentW - 4) / 2;
  drawInfoCard(doc, MARGIN.left, y, halfW, 38, 'Machine Owner', [
    ['Name', ownerName],
    ['PAN', data.ownerPan],
    ['Aadhaar', data.ownerAadhaar],
    ['Bank A/C', data.ownerBank],
    ['IFSC', data.ownerIfsc],
  ]);
  drawInfoCard(doc, MARGIN.left + halfW + 4, y, halfW, 38, 'Machine & Work', [
    ['Machine', data.machineName],
    ['Reg. No.', data.regNo],
    ['Type', data.machineType],
    ['Client', data.clientName],
    ['Work Period', data.workPeriod],
  ]);
  y += 44;

  autoTable(doc, {
    startY: y,
    head: [['Particulars', 'Calculation', 'Amount (Rs)']],
    body: [
      ['Gross Billing (Base)',
        data.finalBase > 0 && data.hours > 0
          ? `${formatHoursHm(data.hours)} × Rs ${data.ratePerHour}/hr`
          : 'Per settlement ledger',
        fmtINR(data.finalBase > 0
          ? data.finalBase
          : gross)],
      ...(data.finalGst > 0 ? [
        ['GST @ 18% on Base',
          'Per booking rate',
          fmtINR(data.finalGst)],
      ] : []),
      [`Platform Commission (${effectiveCommPct}%)`, useDbAmounts ? 'Per settlement ledger' : `${fmtINR(gross)} × ${effectiveCommPct}%`, `(${fmtINR(comm)})`],
      ['TDS Deduction', useDbAmounts ? 'Per settlement ledger' : `${fmtINR(gross)} × 2%`, `(${fmtINR(tds)})`],
      ['GST TCS', useDbAmounts ? 'Per settlement ledger' : `${fmtINR(gross)} × 1%`, `(${fmtINR(gstTcs)})`],
    ],
    foot: [['NET AMOUNT PAID TO OWNER', 'After all statutory deductions', fmtINR(net)]],
    styles: {
      font: 'helvetica', fontSize: 7.5, cellPadding: 3.5, valign: 'middle',
      overflow: 'linebreak',
      lineColor: rgb(BRAND.BORDER), lineWidth: 0.1,
    },
    headStyles: {
      fillColor: rgb(BRAND.GOLD_PALE), textColor: rgb(BRAND.GOLD_DARK),
      fontStyle: 'bold', fontSize: 7,
    },
    footStyles: {
      fillColor: rgb(BRAND.EMERALD), textColor: [255, 255, 255],
      fontStyle: 'bold', fontSize: 8,
    },
    alternateRowStyles: { fillColor: rgb(BRAND.ROW_ALT) },
    columnStyles: {
      0: { cellWidth: 78, fontStyle: 'bold', textColor: rgb(BRAND.INK) },
      1: { cellWidth: 62, textColor: rgb(BRAND.MUTED) },
      2: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
    },
    margin: {
      top: MARGIN.top,
      bottom: MARGIN.bottom,
      left: MARGIN.left,
      right: MARGIN.right,
    },
    didParseCell: (hook) => {
      if (hook.section === 'body' && hook.column.index === 2) {
        hook.cell.styles.textColor = hook.row.index === 0 ? rgb(BRAND.EMERALD) : rgb(BRAND.RUBY);
      }
      if (hook.section === 'foot' && hook.column.index === 2) {
        hook.cell.styles.textColor = [255, 255, 255];
        hook.cell.styles.fontSize = 9;
      }
    },
    didDrawPage: () => {
      const page = doc.internal.getCurrentPageInfo?.().pageNumber
        ?? doc.internal.getNumberOfPages();
      if (page < 2) return;
      drawInsideHeaderFooter(
        doc,
        page,
        doc.internal.getNumberOfPages(),
        receiptNo,
        docHash,
      );
    },
  });

  y = doc.lastAutoTable.finalY + 6;

  ensureSpace(35);
  roundRectFS(doc, MARGIN.left, y, contentW, 16, 1.5, BRAND.GOLD_PALE, BRAND.GOLD, 0.3);
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('AMOUNT IN WORDS', MARGIN.left + 3, y + 5);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const wordLines = doc.splitTextToSize(String(data.words || ''), contentW - 6);
  doc.text(wordLines[0], MARGIN.left + 3, y + 11);

  y += 22;

  ensureSpace(35);
  roundRectFS(doc, MARGIN.left, y, contentW, 22, 1.5, '#FFFFFF', BRAND.BORDER, 0.25);
  doc.setFillColor(...rgb(BRAND.GOLD_PALE));
  doc.rect(MARGIN.left, y, contentW, 6, 'F');
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('PAYMENT TRANSFER DETAILS', MARGIN.left + 3, y + 4.2);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(`Mode: ${data.paymentMode}`, MARGIN.left + 3, y + 11);
  doc.text(`Ref: ${data.paymentRef}`, MARGIN.left + 55, y + 11);
  doc.text(`Date: ${payDate}`, MARGIN.left + 115, y + 11);
  doc.text(`Beneficiary: ${ownerName}`, MARGIN.left + 3, y + 17);
  doc.text(`Credited to: ${data.walletDestination}`, MARGIN.left + 3, y + 21);

  y += 28;

  ensureSpace(35);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('This receipt confirms full and final settlement for the stated work period.', MARGIN.left, y);
  doc.text('TDS Form 16A will be issued quarterly. GST TCS deducted as per Govt. of India regulations.', MARGIN.left, y + 4);
  doc.setTextColor(...rgb(BRAND.RUBY));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('This is a Payment Receipt, not a GST Tax Invoice', MARGIN.left, y + 9);
  y += 16;

  // Dedicated last page for digital e-sign
  doc.addPage();
  y = MARGIN.top;

  const now = new Date();
  const istParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const part = (type) => istParts.find((p) => p.type === type)?.value || '';
  const esignTimestamp = `${part('day')}/${part('month')}/${part('year')} ${part('hour')}:${part('minute')} IST`;

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Digital Signatures', MARGIN.left, y);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('IT Act 2000 Sec 3A - Digitally Verified via Platform', MARGIN.left, y + 5);
  y += 14;

  const sigW = (contentW - 8) / 2;
  const sigH = 42;
  const sigBoxes = [
    {
      role: 'Owner Acknowledgement',
      name: ownerName,
      detail: 'Digitally Verified via Platform',
    },
    {
      role: 'For Development Express',
      name: 'Om Chavan, Managing Director',
      detail: 'Digitally Verified via Platform',
    },
  ];
  sigBoxes.forEach((s, i) => {
    const x = MARGIN.left + i * (sigW + 8);
    roundRectFS(doc, x, y, sigW, sigH, 1.5, '#FFFFFF', BRAND.GOLD, 0.45);
    doc.setFillColor(...rgb(BRAND.GOLD_PALE));
    doc.rect(x, y, sigW, 7, 'F');
    doc.setDrawColor(...rgb(BRAND.GOLD));
    doc.setLineWidth(0.2);
    doc.line(x, y + 7, x + sigW, y + 7);

    doc.setTextColor(...rgb(BRAND.GOLD_DARK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(s.role.toUpperCase(), x + 3, y + 4.8);

    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const nameLines = doc.splitTextToSize(String(s.name || '—'), sigW - 6);
    doc.text(nameLines[0], x + 3, y + 16);

    doc.setTextColor(...rgb(BRAND.EMERALD));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(s.detail, x + 3, y + 26);

    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(`Timestamp: ${esignTimestamp}`, x + 3, y + 34);
  });
  y += sigH + 10;

  const certW = Math.min(140, contentW);
  const certX = MARGIN.left + (contentW - certW) / 2;
  const certH = 28;
  roundRectFS(doc, certX, y, certW, certH, 1.5, BRAND.GOLD_PALE, BRAND.GOLD, 0.35);
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('Digital Signature Certificate', certX + certW / 2, y + 6, { align: 'center' });
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('Powered by eMudhra - CCA Licensed CA', certX + certW / 2, y + 12, { align: 'center' });
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFontSize(6);
  doc.text('Certificate ID: [pending e-Sign integration]', certX + certW / 2, y + 18, { align: 'center' });
  doc.text('This document is digitally signed under IT Act 2000, Sec 3A', certX + certW / 2, y + 23.5, { align: 'center' });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    drawInsideHeaderFooter(doc, i, totalPages, receiptNo, docHash);
  }

  const safeId = String(bookingId).replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 40);
  const filename = `DE_Owner_Receipt_PREMIUM_${safeId}.pdf`;
  if (download) doc.save(filename);
  doc.__deFilename = filename;
  return doc;
}
